import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";

export default function Footer() {
  const [settings, setSettings] = useState(null);
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/industries").then((r) => setIndustries(r.data || [])).catch(() => {});
  }, []);

  return (
    <footer className="bg-[var(--navy)] text-[#E2E8F0] mt-16 sm:mt-24" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          <div className="md:col-span-4">
            <Logo size="md" inverse />
            <p className="mt-6 font-display italic text-lg leading-snug text-white/90">
              Strategic Intelligence for High-Growth Industries.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-white/65 max-w-sm">
              Luminary helps leaders navigate uncertainty, identify growth opportunities, and make
              high-impact strategic decisions across chemicals, packaging, energy, semiconductors,
              manufacturing, and emerging technologies.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {settings?.social?.linkedin && (
                <a
                  href={settings.social.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 border border-white/30 flex items-center justify-center hover:bg-[var(--gold)] hover:border-[var(--gold)] hover:text-[var(--navy)] transition-colors"
                  data-testid="footer-linkedin"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={15} />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-[var(--gold)]">Industries</div>
            <ul className="mt-4 space-y-2 text-sm">
              {industries.slice(0, 8).map((i) => (
                <li key={i.slug}>
                  <Link to={`/industries/${i.slug}`} className="text-white/80 hover:text-[var(--gold)]">{i.name}</Link>
                </li>
              ))}
              <li>
                <Link to="/industries" className="text-[var(--gold)] hover:underline">View all 12 →</Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-[var(--gold)]">Capabilities</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/modules" className="text-white/80 hover:text-[var(--gold)]">Intelligence Modules</Link></li>
              <li><Link to="/frameworks" className="text-white/80 hover:text-[var(--gold)]">Frameworks</Link></li>
              <li><Link to="/trackers" className="text-white/80 hover:text-[var(--gold)]">Trackers</Link></li>
              <li><Link to="/luminous" className="text-white/80 hover:text-[var(--gold)]">Luminous AI</Link></li>
              <li><Link to="/briefings" className="text-white/80 hover:text-[var(--gold)]">Briefings</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-[var(--gold)]">Firm</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/firm" className="text-white/80 hover:text-[var(--gold)]">About</Link></li>
              <li><Link to="/methodology" className="text-white/80 hover:text-[var(--gold)]">Methodology</Link></li>
              <li><Link to="/team" className="text-white/80 hover:text-[var(--gold)]">Practice Leads</Link></li>
              <li><Link to="/press" className="text-white/80 hover:text-[var(--gold)]">Press</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-[var(--gold)]">Engage</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/strategy-session" className="text-[var(--gold)] hover:underline">Schedule Strategy Session →</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-[var(--gold)]">Contact</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-10 border-t border-white/15 grid md:grid-cols-2 gap-10">
          <div data-testid="footer-practice">
            <div className="eyebrow text-[var(--gold)]">Practice</div>
            <p className="mt-3 text-sm leading-relaxed text-white/80">
              Remote-first strategic intelligence &amp; advisory desk operating globally across 14 industries.
            </p>
            <p className="mt-2 text-sm font-mono text-white/60">Response within one business day</p>
          </div>
          <div>
            <div className="eyebrow text-[var(--gold)]">Contact</div>
            <a
              href={settings?.contact_email ? `mailto:${settings.contact_email}` : "#"}
              className="mt-3 inline-flex items-center gap-1 text-sm font-mono tracking-wider text-white/80 hover:text-[var(--gold)]"
              data-testid="footer-contact-email"
            >
              {settings?.contact_email || "support@luminaryin.com"} <ArrowUpRight size={12} />
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/15 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-white/55">
            © {new Date().getFullYear()} Luminary Insights &amp; Advisory.  All rights reserved.
          </div>
          <div className="flex gap-5 text-xs font-mono uppercase tracking-[0.18em] text-white/55">
            <Link to="/contact" className="hover:text-[var(--gold)]">Privacy</Link>
            <Link to="/contact" className="hover:text-[var(--gold)]">Terms</Link>
            <Link to="/contact" className="hover:text-[var(--gold)]">Disclosures</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
